import axios from 'axios';
import type {
    Account,
    FeeQuote,
    GaslessTransaction,
    IEthereumProvider,
    RequestArguments,
    SmartAccountConfig,
    Transaction,
    UserPaidTransaction,
    WalletTransaction,
} from './types';
import { UserOperation } from './types';
import { payloadId, rpcUrl } from './utils';

export class SmartAccount {
    constructor(public provider: IEthereumProvider, private config: SmartAccountConfig) {}

    private connection = axios.create({
        baseURL: `${rpcUrl()}/evm-chain`,
        timeout: 60_000,
    });

    private getChainId = async (): Promise<string> => {
        return await this.provider.request({ method: 'eth_chainId' });
    };

    private async getDappAPIKey(): Promise<string> {
        const chainId = await this.getChainId();
        const networkConfig = this.config.networkConfig.find((item) => item.chainId === Number(chainId));
        if (networkConfig) {
            return networkConfig.dappAPIKey;
        }
        return Promise.reject(
            new Error(`Current chain is not supported, chainId: ${chainId}, please configure it first`)
        );
    }

    private async checkCurrentChain() {
        await this.getDappAPIKey();
    }

    async getFeeQuotes(tx: Transaction | Transaction[]): Promise<FeeQuote[]> {
        await this.checkCurrentChain();

        const eoa = (await this.provider.request({ method: 'eth_accounts' }))[0];
        const feeQuotes = await this.sendRpc<FeeQuote[]>({
            method: 'particle_biconomy_getFeeQuotes',
            params: ['1.0.0', eoa, Array.isArray(tx) ? tx : [tx]],
        });
        return feeQuotes;
    }

    async createUserPaidTransaction(tx: Transaction | Transaction[], feeQuote: FeeQuote): Promise<UserPaidTransaction> {
        await this.checkCurrentChain();

        const eoas = await this.provider.request({ method: 'eth_accounts' });
        return await this.sendRpc<UserPaidTransaction>({
            method: 'particle_biconomy_createUserPaidTransaction',
            params: ['1.0.0', eoas[0], Array.isArray(tx) ? tx : [tx], feeQuote],
        });
    }

    async sendUserPaidSignedTransaction(walletTx: WalletTransaction, signature: string): Promise<string> {
        await this.checkCurrentChain();

        const eoas = await this.provider.request({ method: 'eth_accounts' });
        const hash = await this.sendRpc<string>({
            method: 'particle_biconomy_sendUserPaidTransaction',
            params: ['1.0.0', eoas[0], walletTx, signature],
        });
        return hash;
    }

    async sendUserPaidTransaction(tx: Transaction | Transaction[], feeQuote: FeeQuote): Promise<string> {
        await this.checkCurrentChain();

        const eoas = await this.provider.request({ method: 'eth_accounts' });
        const userPaidTx = await this.createUserPaidTransaction(tx, feeQuote);
        const signature = await this.provider.request({
            method: 'eth_signTypedData_v4',
            params: [eoas[0], JSON.stringify(userPaidTx.typedData)],
        });
        return this.sendUserPaidSignedTransaction(userPaidTx.walletTransaction, signature);
    }

    async createGaslessTransaction(tx: Transaction | Transaction[]): Promise<GaslessTransaction> {
        const apiKey = await this.getDappAPIKey();
        const eoas = await this.provider.request({ method: 'eth_accounts' });
        const gaslessTx = await this.sendRpc<GaslessTransaction>({
            method: 'particle_biconomy_createGaslessTransaction',
            params: ['1.0.0', eoas[0], Array.isArray(tx) ? tx : [tx], apiKey],
        });
        return gaslessTx;
    }

    async sendGaslessSignedTransaction(userOp: UserOperation, signature: string): Promise<string> {
        const apiKey = await this.getDappAPIKey();
        const eoas = await this.provider.request({ method: 'eth_accounts' });
        return await this.sendRpc<string>({
            method: 'particle_biconomy_sendGaslessTransaction',
            params: [
                '1.0.0',
                eoas[0],
                {
                    ...userOp,
                    signature,
                },
                apiKey,
            ],
        });
    }

    async sendGaslessTransaction(tx: Transaction | Transaction[]): Promise<string> {
        const gaslessTx = await this.createGaslessTransaction(tx);
        const eoas = await this.provider.request({ method: 'eth_accounts' });
        const signature = await this.provider.request({
            method: 'personal_sign',
            params: [gaslessTx.userOpHash, eoas[0]],
        });

        return await this.sendGaslessSignedTransaction(gaslessTx.userOp, signature);
    }

    async getAccount(): Promise<Account> {
        const eoas = await this.provider.request({ method: 'eth_accounts' });
        const accounts = await this.sendRpc<Account[]>({
            method: 'particle_biconomy_getSmartAccount',
            params: ['1.0.0', eoas],
        });
        return accounts[0];
    }

    async getAddress(): Promise<string> {
        const eoas = await this.provider.request({ method: 'eth_accounts' });
        if (!eoas || eoas.length === 0) {
            return '';
        }
        if (typeof window !== 'undefined' && localStorage) {
            const localAA = localStorage.getItem(`particle_biconomy_${eoas[0]}`);
            if (localAA) {
                return localAA;
            }
        }

        const account = await this.getAccount();
        const address = account.smartAccountAddress;
        if (typeof window !== 'undefined' && localStorage) {
            localStorage.setItem(`particle_biconomy_${eoas[0]}`, address);
        }
        return address;
    }

    async getOwner(): Promise<string> {
        const eoas = await this.provider.request({ method: 'eth_accounts' });
        return eoas[0];
    }

    async isDeployed(): Promise<boolean> {
        const account = await this.getAccount();
        return account.isDeployed;
    }

    async deployWalletContract(): Promise<string> {
        return await this.sendGaslessTransaction({
            to: '0x0000000000000000000000000000000000000000',
            data: '0x',
        });
    }

    async sendRpc<T>(arg: RequestArguments): Promise<T> {
        const chainId = await this.getChainId();
        const response = await this.connection
            .post(
                '',
                {
                    ...arg,
                    id: payloadId(),
                    jsonrpc: '2.0',
                },
                {
                    params: {
                        chainId: Number(chainId),
                        projectUuid: this.config.projectId,
                        projectKey: this.config.clientKey,
                    },
                }
            )
            .then((res) => res.data);
        if (response.error) {
            return Promise.reject(response.error);
        } else {
            return response.result;
        }
    }
}
